// L03 (G-C3, G-C4) — take a payment on a customer's credit account at the till,
// without a sale. One amount settles the oldest open invoices first, or the
// cashier picks one invoice. Cash / UPI / Card; online only. The server answers
// a receipt; the done screen prints it (thermal printer, else the server PDF).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, GLASS, cn } from '@bbpos/ui';
import { getCurrencyDecimals, getSubunitMultiplier, majorToAmount } from '@bbpos/utils';
import {
  creditAccountService,
  type OnAccountMethod,
  type OnAccountPaymentReceipt,
  type OpenCreditInvoice,
} from '@/services/credit-account.service';
import { getActiveRegisterSessionId } from '@/stores/use-register-store';
import { usePrinterStore } from '@/stores/use-printer-store';
import { useTenantStore } from '@/stores/use-tenant-store';
import { useLocationStore } from '@/stores/use-location-store';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { formatCurrency } from '@/lib/format';

export interface ReceiveOnAccountModalProps {
  accountId: string;
  customerName: string;
  /** What the account owes now, minor units. */
  owes: number;
  locationId: string;
  onClose: () => void;
  /** The payment went through: the new balance is in `result.account`. */
  onPaid: (result: OnAccountPaymentReceipt) => void;
}

type Settle = 'oldest' | 'one';

const METHODS: Array<{ id: OnAccountMethod; label: string }> = [
  { id: 'CASH', label: 'Cash' },
  { id: 'UPI', label: 'UPI' },
  { id: 'CARD', label: 'Card' },
];

/** Oldest first: what each invoice gets from `amount` — the server's own walk, for display only. */
export function previewOldestFirst(invoices: OpenCreditInvoice[], amount: number) {
  let left = amount;
  const rows: Array<{ invoice: OpenCreditInvoice; pay: number; after: number }> = [];
  for (const invoice of invoices) {
    if (left <= 0) break;
    const pay = Math.min(left, invoice.balance);
    if (pay <= 0) continue;
    rows.push({ invoice, pay, after: invoice.balance - pay });
    left -= pay;
  }
  return rows;
}

function toInput(minor: number, currency: string): string {
  return (minor / getSubunitMultiplier(currency)).toFixed(getCurrencyDecimals(currency));
}

export function ReceiveOnAccountModal({
  accountId,
  customerName,
  owes,
  locationId,
  onClose,
  onPaid,
}: ReceiveOnAccountModalProps) {
  const currency = useTenantCurrency();
  const fmt = useCallback((minor: number) => formatCurrency(minor, currency), [currency]);

  const [invoices, setInvoices] = useState<OpenCreditInvoice[] | null>(null);
  const [settle, setSettle] = useState<Settle>('oldest');
  const [invoiceId, setInvoiceId] = useState<string>('');
  const [amountText, setAmountText] = useState(() => toInput(owes, currency));
  const [method, setMethod] = useState<OnAccountMethod>('CASH');
  const [reference, setReference] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<OnAccountPaymentReceipt | null>(null);
  // One key per opening of this window, reused on a retry: a resend is
  // replayed by the server, never taken twice.
  const idempotencyKey = useRef<string>(crypto.randomUUID());

  useEffect(() => {
    let cancelled = false;
    creditAccountService
      .listOpenInvoices(accountId)
      .then((rows) => {
        if (!cancelled) setInvoices(rows);
      })
      .catch(() => {
        if (!cancelled) setInvoices([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  const picked = settle === 'one' ? (invoices?.find((i) => i.id === invoiceId) ?? null) : null;
  const invoicesOwe =
    invoices && invoices.length > 0 ? invoices.reduce((sum, i) => sum + i.balance, 0) : null;
  // The server caps at what the open invoices owe; the account figure until they load.
  const cap = settle === 'one' ? (picked?.balance ?? 0) : (invoicesOwe ?? owes);

  const amount = useMemo(() => {
    const n = Number(amountText.trim());
    return Number.isFinite(n) && n > 0 ? majorToAmount(n, currency) : 0;
  }, [amountText, currency]);
  const tooMuch = amount > cap;
  const canSend = !sending && amount > 0 && !tooMuch && (settle === 'oldest' || !!picked);
  const preview =
    settle === 'oldest' && invoices ? previewOldestFirst(invoices, Math.min(amount, cap)) : [];

  const pickInvoice = (id: string) => {
    setInvoiceId(id);
    const inv = invoices?.find((i) => i.id === id);
    if (inv) setAmountText(toInput(inv.balance, currency));
  };

  const chooseSettle = (next: Settle) => {
    setSettle(next);
    if (next === 'oldest') setAmountText(toInput(invoicesOwe ?? owes, currency));
  };

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setError(null);
    try {
      const registerSessionId = getActiveRegisterSessionId(locationId);
      const res = await creditAccountService.receivePayment(
        accountId,
        {
          amount,
          payment_method: method,
          ...(method !== 'CASH' && reference.trim() ? { reference_number: reference.trim() } : {}),
          location_id: locationId,
          ...(registerSessionId ? { register_session_id: registerSessionId } : {}),
          ...(picked ? { credit_invoice_id: picked.id } : {}),
        },
        idempotencyKey.current,
      );
      setResult(res);
      onPaid(res);
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Could not take the payment. Try again.',
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Receive payment"
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/60 backdrop-blur-sm p-4 animate-fade-in"
      onClick={sending ? undefined : onClose}
    >
      <div
        className={cn(
          GLASS,
          'max-w-md w-full overflow-hidden animate-scale-in max-h-[92vh] overflow-y-auto',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-4 text-white">
          <h2 className="text-lg font-bold">{result ? 'Payment received' : 'Receive payment'}</h2>
          <p className="text-sm text-indigo-100 truncate">{customerName}</p>
          {!result && (
            <p className="mt-1 text-sm">
              Owes{' '}
              <span data-testid="receive-owes" className="font-bold tabular-nums">
                {fmt(owes)}
              </span>
            </p>
          )}
        </div>

        {result ? (
          <DoneScreen result={result} fmt={fmt} onClose={onClose} />
        ) : (
          <div className="space-y-4 p-5">
            <div>
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                Settle
              </span>
              <div className="grid grid-cols-2 gap-2">
                {(['oldest', 'one'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={settle === s}
                    onClick={() => chooseSettle(s)}
                    className={cn(
                      'rounded-lg border px-3 py-2 text-sm font-medium transition-all duration-200',
                      settle === s
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200'
                        : 'border-neutral-200 text-neutral-700 hover:bg-indigo-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800',
                    )}
                  >
                    {s === 'oldest' ? 'Oldest first' : 'One invoice'}
                  </button>
                ))}
              </div>
            </div>

            {settle === 'one' && (
              <div className="space-y-1" role="radiogroup" aria-label="Invoice to pay">
                {invoices === null && <p className="text-sm text-neutral-500">Loading invoices…</p>}
                {invoices?.length === 0 && (
                  <p className="text-sm text-neutral-500">No open invoices.</p>
                )}
                {invoices?.map((inv) => (
                  <label
                    key={inv.id}
                    className={cn(
                      'flex cursor-pointer items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm transition-all duration-200 even:bg-neutral-50 hover:bg-indigo-50 dark:even:bg-neutral-800/50 dark:hover:bg-neutral-800',
                      invoiceId === inv.id
                        ? 'border-indigo-500'
                        : 'border-neutral-200 dark:border-neutral-700',
                    )}
                  >
                    <span className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="receive-invoice"
                        value={inv.id}
                        checked={invoiceId === inv.id}
                        onChange={() => pickInvoice(inv.id)}
                      />
                      <span>
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {inv.invoice_number}
                        </span>
                        <span className="block text-xs text-neutral-500 dark:text-neutral-400">
                          {new Date(inv.created_at).toLocaleDateString()}
                        </span>
                      </span>
                    </span>
                    <span className="font-semibold tabular-nums text-neutral-900 dark:text-white">
                      {fmt(inv.balance)}
                    </span>
                  </label>
                ))}
              </div>
            )}

            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                Amount
              </span>
              <input
                type="text"
                inputMode="decimal"
                aria-label="Amount"
                value={amountText}
                onChange={(e) => setAmountText(e.target.value)}
                className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-lg font-semibold tabular-nums text-neutral-900 focus:border-indigo-500 focus:outline-none dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
              />
              {tooMuch && (
                <span
                  role="alert"
                  className="mt-1 block text-xs text-danger-600 dark:text-danger-400"
                >
                  {settle === 'one'
                    ? `More than the ${fmt(cap)} left on this invoice`
                    : `More than the ${fmt(cap)} they owe`}
                </span>
              )}
            </label>

            {preview.length > 0 && (
              <ul
                data-testid="receive-preview"
                className="space-y-1 rounded-lg bg-indigo-50/60 p-2 text-xs dark:bg-indigo-950/30"
              >
                {preview.map((row) => (
                  <li key={row.invoice.id} className="flex justify-between gap-2 animate-slide-up">
                    <span className="text-neutral-700 dark:text-neutral-200">
                      {row.invoice.invoice_number}
                    </span>
                    <span className="tabular-nums text-neutral-900 dark:text-white">
                      {fmt(row.pay)} paid · {row.after > 0 ? `${fmt(row.after)} left` : 'Paid'}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            <div>
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                Method
              </span>
              <div className="grid grid-cols-3 gap-2">
                {METHODS.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    aria-pressed={method === m.id}
                    onClick={() => setMethod(m.id)}
                    className={cn(
                      'rounded-lg border px-3 py-2 text-sm font-medium transition-all duration-200',
                      method === m.id
                        ? 'border-violet-500 bg-violet-50 text-violet-700 dark:bg-violet-900/40 dark:text-violet-200'
                        : 'border-neutral-200 text-neutral-700 hover:bg-violet-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800',
                    )}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {method !== 'CASH' && (
              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  Reference (optional)
                </span>
                <input
                  type="text"
                  aria-label="Reference"
                  maxLength={100}
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  className="w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 focus:border-indigo-500 focus:outline-none dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
                />
              </label>
            )}

            {error && (
              <p
                role="alert"
                className="rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700 dark:bg-danger-900/30 dark:text-danger-300"
              >
                {error}
              </p>
            )}

            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1" onClick={onClose} disabled={sending}>
                Cancel
              </Button>
              <Button
                className="flex-1 bg-gradient-to-r from-indigo-600 to-violet-600 text-white"
                onClick={() => void send()}
                disabled={!canSend}
              >
                {sending ? 'Taking payment…' : 'Take payment'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DoneScreen({
  result,
  fmt,
  onClose,
}: {
  result: OnAccountPaymentReceipt;
  fmt: (minor: number) => string;
  onClose: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [printed, setPrinted] = useState(false);
  const currency = useTenantCurrency();
  const tenant = useTenantStore((s) => s.tenant);
  const currentLocation = useLocationStore((s) => s.currentLocation);
  const printerStatus = usePrinterStore((s) => s.connectionStatus);
  const printerType = usePrinterStore((s) => s.connectionType);
  const paperWidth = usePrinterStore((s) => s.paperWidth);
  const printPaymentReceipt = usePrinterStore((s) => s.printPaymentReceipt);
  const kickCashDrawer = usePrinterStore((s) => s.kickCashDrawer);
  const openCashDrawer = usePrinterStore((s) => s.openCashDrawer);
  // Same gate as the sale receipt: the 'browser' transport cannot render ESC/POS.
  const isPrinterReady = printerStatus === 'connected' && printerType !== 'browser';
  const r = result.receipt;

  // As after a cash sale: kick the drawer once when the till is set to.
  const kicked = useRef(false);
  useEffect(() => {
    if (kicked.current) return;
    kicked.current = true;
    if (isPrinterReady && kickCashDrawer && r.payment_method === 'CASH') void openCashDrawer();
  }, [isPrinterReady, kickCashDrawer, openCashDrawer, r.payment_method]);

  const print = async () => {
    setError(null);
    if (isPrinterReady) {
      printPaymentReceipt(
        {
          receipt_number: r.receipt_number,
          created_at: r.created_at,
          customer_name: r.customer_name,
          allocations: result.allocations.map((a) => ({
            invoice_number: a.invoice_number,
            amount: a.amount,
          })),
          amount: r.amount,
          payment_method: r.payment_method,
          reference_number: r.reference_number,
          now_owes: result.account.outstanding_balance,
          cashier_name: r.cashier_name,
          tenant: { business_name: tenant?.business_name ?? '' },
          location: currentLocation
            ? {
                name: currentLocation.name,
                address: currentLocation.address ?? null,
                city: currentLocation.city ?? null,
                state: currentLocation.state ?? null,
                pincode: currentLocation.pincode ?? null,
                phone: currentLocation.phone ?? null,
              }
            : null,
        },
        r.id,
        currency,
      );
      setPrinted(true);
      setTimeout(() => setPrinted(false), 3000);
      return;
    }
    // No thermal printer — the PDF is the way to get paper in hand.
    setBusy(true);
    try {
      const format = paperWidth === 58 ? '58mm' : '80mm';
      const blob = await creditAccountService.getPaymentReceiptPdf(r.id, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${r.receipt_number}-${format}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setError('Could not download the receipt PDF.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 p-5 animate-fade-in">
      <dl className="grid grid-cols-2 gap-2 text-sm">
        <dt className="text-neutral-500 dark:text-neutral-400">Receipt</dt>
        <dd
          data-testid="receive-receipt-number"
          className="text-right font-mono text-neutral-900 dark:text-white"
        >
          {r.receipt_number}
        </dd>
        <dt className="text-neutral-500 dark:text-neutral-400">Paid</dt>
        <dd className="text-right font-semibold tabular-nums text-neutral-900 dark:text-white">
          {fmt(r.amount)}
        </dd>
        <dt className="text-neutral-500 dark:text-neutral-400">Method</dt>
        <dd className="text-right text-neutral-900 dark:text-white">
          {r.payment_method}
          {r.reference_number ? ` · ${r.reference_number}` : ''}
        </dd>
      </dl>
      <ul className="space-y-1 rounded-lg bg-indigo-50/60 p-2 text-xs dark:bg-indigo-950/30">
        {result.allocations.map((a) => (
          <li key={a.credit_invoice_id} className="flex justify-between gap-2">
            <span className="text-neutral-700 dark:text-neutral-200">{a.invoice_number}</span>
            <span className="tabular-nums text-neutral-900 dark:text-white">
              {fmt(a.amount)} · {a.balance_after > 0 ? `${fmt(a.balance_after)} left` : 'Paid'}
            </span>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between rounded-lg bg-gradient-to-r from-indigo-50 to-violet-50 px-3 py-2 dark:from-indigo-950/40 dark:to-violet-950/40">
        <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Now owes</span>
        <span
          data-testid="receive-now-owes"
          className="text-lg font-bold tabular-nums text-neutral-900 dark:text-white"
        >
          {fmt(result.account.outstanding_balance)}
        </span>
      </div>
      {error && (
        <p role="alert" className="text-sm text-danger-600 dark:text-danger-400">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button variant="secondary" className="flex-1" onClick={() => void print()} disabled={busy}>
          {printed ? 'Sent to printer' : busy ? 'Getting PDF…' : 'Print receipt'}
        </Button>
        <Button
          className="flex-1 bg-gradient-to-r from-indigo-600 to-violet-600 text-white"
          onClick={onClose}
        >
          Done
        </Button>
      </div>
    </div>
  );
}
